import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Layers } from "lucide-react";

const CHART =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/06zm5ahp_COTTON%20ROUND%20NECK%20COLOUR%20CHART.jpg";

const products = [
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/qyoqjhw3_COTTON%20ROUND%20NECK%20%283%29.jpg",
    name: "Black",
    swatch: "#0A0A0A",
    fg: "#FFFFFF",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/as779up7_COTTON%20ROUND%20NECK%20%282%29.jpg",
    name: "Grey Melange",
    swatch: "#9CA3AF",
    fg: "#0A0A0A",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/wze8b83x_COTTON%20ROUND%20NECK%20%285%29.jpg",
    name: "White",
    swatch: "#FFFFFF",
    fg: "#0A0A0A",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/nlygcsf9_COTTON%20ROUND%20NECK%20%286%29.jpg",
    name: "Maroon",
    swatch: "#7E1E2A",
    fg: "#FFFFFF",
  },
];

const specs = [
  "180 GSM",
  "100% Cotton",
  "Bio-Washed",
  "Regular Fit",
  "Round Neck",
  "Sizes XS – 5XL",
];

export default function Blanks() {
  return (
    <section
      id="blanks"
      className="border-b-4 border-black py-20 lg:py-28 bg-[#FDFDFD]"
      data-testid="blanks-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-4">
              /// Cotton blanks · House line
            </div>
            <h2 className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              <span className="bg-[#FFE600] nb-border-2 px-2 inline-block">22 shades.</span>
              <br />
              One perfect tee.
            </h2>
            <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#0A0A0A]/80">
              Our in-house unisex round-neck t-shirt — 180 GSM bio-washed 100%
              cotton, regular fit, sizes XS to 5XL. Order blanks for resale or
              get them customised with DTF prints, embroidery, screen-print or
              full-fabric sublimation. Shipped pan-India from our Mumbai
              workshop.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-5 flex flex-wrap items-end gap-2 content-end">
            {specs.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 nb-border-2 bg-white px-3 py-1.5 font-heading uppercase text-xs tracking-wider"
                data-testid={`spec-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Check size={12} strokeWidth={3} className="text-[#FF0055]" />
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Colour chart hero */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative nb-border-4 nb-shadow-lg overflow-hidden bg-white"
          data-testid="blanks-colour-chart"
        >
          <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 bg-[#FF0055] text-white nb-border-2 px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
            <Layers size={12} strokeWidth={3} /> Full Colour Chart
          </div>
          <img
            src={CHART}
            alt="Customade cotton round-neck t-shirt colour chart — 20+ shades"
            className="w-full h-auto block"
            loading="lazy"
          />
        </motion.div>

        {/* Product hangers */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {products.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="nb-tile nb-border-4 nb-shadow-sm bg-white aspect-[3/4]"
              data-testid={`blank-product-${i}`}
            >
              <img
                src={p.url}
                alt={`Cotton round-neck t-shirt in ${p.name}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                className="absolute bottom-3 left-3 right-3 nb-border-2 px-3 py-2 flex items-center justify-between gap-2"
                style={{ background: p.swatch, color: p.fg }}
              >
                <span className="font-heading uppercase text-xs tracking-wider truncate">
                  {p.name}
                </span>
                <span
                  className="w-3 h-3 nb-border-2 flex-shrink-0"
                  style={{ background: p.swatch, borderColor: p.fg }}
                  aria-hidden
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 nb-border-4 bg-black text-white p-5 md:p-7">
          <div>
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#FFE600] mb-1">
              Pricing tip
            </div>
            <div className="font-heading uppercase text-xl md:text-2xl leading-tight">
              Better rates start at 50 pcs · Mix &amp; match colours.
            </div>
          </div>
          <a href="#contact" className="nb-btn" data-testid="blanks-cta">
            Get bulk pricing <ArrowRight size={18} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}
